import React from 'react';
import { useController } from 'react-hook-form';
import { createEditor, Descendant, Editor, Transforms, Element as SlateElement, Node, Range } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import { Box, Progress } from '@chakra-ui/react';
import { Toolbar } from './Toolbar';
import { useDebounce } from '~/hooks/useDebounce';
import {
    parseJsonRecursively,
    replaceHtml,
    serialize,
    serializedBudgetPercent,
} from '~/utils/richText';
import { get, isEqual } from 'lodash';
import log from '~/log';

const Element = ({ attributes = {}, children, element }: any) => {
    switch (element.type) {
        case 'block-quote':
            return <blockquote {...attributes}>{children}</blockquote>;
        case 'bulleted-list':
            return <ul {...attributes}>{children}</ul>;
        case 'heading-one':
            return <h1 {...attributes}>{children}</h1>;
        case 'heading-two':
            return <h2 {...attributes}>{children}</h2>;
        case 'list-item':
            return <li {...attributes}>{children}</li>;
        case 'numbered-list':
            return <ol {...attributes}>{children}</ol>;
        case 'page-break':
            return (
                <div {...attributes}>
                    {children}
                    <hr contentEditable={false} style={{ userSelect: 'none' }} />
                </div>
            );
        default:
            return <p {...attributes}>{children}</p>;
    }
};

const Leaf = ({ attributes = {}, children, leaf }: any) => {
    if (leaf.bold) children = <strong>{children}</strong>;
    if (leaf.code) children = <code>{children}</code>;
    if (leaf.italic) children = <em>{children}</em>;
    if (leaf.underline) children = <u>{children}</u>;
    return <span {...attributes}>{children}</span>;
};

const defaultValue: Descendant[] = [{ type: 'paragraph', children: [{ text: '' }] }];

const getDefaultValue = (controlledValue?: any) => {
    let value = controlledValue?.text || controlledValue;

    /** fixes: [Slate] value is invalid! Expected a list of elements but got: "[]" @/einsatzberichte */
    if (value === '[]') return defaultValue;

    const parsedValue = parseJsonRecursively(value);
    if (isValidSlateContent(parsedValue)) {
        return parsedValue;
    }

    /** fixes: [Slate] value is invalid! Expected a list of elements but got: {"text":"[{\"type\":\"paragraph\"... */
    if (parsedValue && typeof parsedValue === 'object' && 'text' in parsedValue) {
        value = parseJsonRecursively((parsedValue as any).text);
    } else {
        value = parsedValue;
    }

    if (
        Array.isArray(value) &&
        value?.[0]?.children?.some(({ text = '' }) => /<[^>]+>|&[a-zA-Z0-9#]+;/g.test(text))
    ) {
        return [
            { type: 'paragraph', children: [{ text: replaceHtml(serialize(value as never[])) }] },
        ];
    } else if (Array.isArray(value) && isValidSlateContent(value)) {
        return value;
    }

    return defaultValue;
};

function isValidSlateContent(nodes: any) {
    return Array.isArray(nodes) && nodes.every((node) => Node.isNode(node));
}

function RichTextEditor({ control, name, ...props }: any) {
    const [key, setKey] = React.useState(() => Date.now());
    const initRef = React.useRef(0);
    const lastEmittedRef = React.useRef<any>(undefined);
    const {
        field: { onChange, value: remoteValue },
        formState: { errors },
    } = useController({ name, control, defaultValue: props?.defaultValue || defaultValue });
    const hasError = get(errors, name);
    const [value, setValue] = React.useState<Descendant[]>(defaultValue);

    React.useEffect(() => {
        if (remoteValue && initRef.current === 0) {
            // Seed only from an external/backend value. Skip the echo of the
            // user's own edit (remoteValue === what we last emitted): seeding
            // it would key-remount the editor mid-typing and drop focus.
            // Comparing values keeps this independent of effect ordering, so
            // the core's on-mount onChange echo can't suppress a real load.
            // (UCPWO-382)
            if (
                !isEqual(remoteValue, defaultValue) &&
                !isEqual(remoteValue, lastEmittedRef.current)
            ) {
                initRef.current = 1;
                const resetValue = (
                    isValidSlateContent(remoteValue) ? remoteValue : getDefaultValue(remoteValue)
                ) as Descendant[];
                log.debug('RichTextEditor.resetValue', resetValue);
                setValue(resetValue);
                setKey(Date.now());
            }
        }
    }, [remoteValue, initRef.current]);

    return (
        <RichTextEditorCore
            hasError={hasError}
            onChange={(value: any) => {
                // TODO:@jfe how can it be a empty array at this point - UCPWO-236
                const next = isEqual(value, []) ? defaultValue : value;
                // Remember what we push into react-hook-form. The seed effect
                // uses this to tell a backend-loaded remoteValue apart from the
                // echo of the user's own edits, so it won't key-remount the
                // editor mid-typing and drop focus. (UCPWO-382)
                lastEmittedRef.current = next;
                onChange?.(next);
            }}
            value={value}
            key={key}
            {...props}
        />
    );
}

function RichTextEditorCore({
    onChange,
    hasSnippets,
    isDisabled = false,
    canBeCopied = true,
    value: remoteValue,
    hasError,
    maxSerializedLength,
    ...props
}: any) {
    const [value, setValue] = React.useState<Descendant[]>(() => remoteValue);
    const debouncedvalue = useDebounce(value, 50);

    const editor = React.useMemo(() => withHistory(withReact(createEditor())), []);
    const renderElement = React.useCallback((props: any) => <Element {...props} />, []);
    const renderLeaf = React.useCallback((props: any) => <Leaf {...props} />, []);

    const divRef = React.useRef<HTMLDivElement>(null);
    const [, setFocused] = React.useState(false);
    const savedSelection = React.useRef(editor.selection);

    // Emit nur bei tatsächlicher Wertänderung: Slate/der defaultValue-Fallback können
    // inhaltsgleiche Werte mit neuer Referenz liefern. Nach einem fehlgeschlagenen
    // Submit gilt reValidateMode 'onChange' — jedes Echo stößt dann einen weiteren
    // Resolver-Lauf an, und die Render-Kaskade verwirft im Prod-Build (ohne
    // StrictMode-Doppelrender) den gerade gesetzten Fehlerzustand wieder (UCPWO-396).
    const lastEmittedRef = React.useRef<any>(undefined);
    React.useEffect(() => {
        if (!isValidSlateContent(debouncedvalue)) return;
        if (isEqual(debouncedvalue, lastEmittedRef.current)) return;
        lastEmittedRef.current = debouncedvalue;
        onChange?.(debouncedvalue);
    }, [debouncedvalue]);

    const onFocus = React.useCallback(() => {
        setFocused(true);
        const sel = savedSelection.current;
        // Only restore a selection that was saved on blur. No Editor.end
        // fallback: it overrode the clicked position with the document end on
        // first focus, making Slate scroll the whole page to the editor's end.
        // Also guard the saved selection against the CURRENT document: a stale
        // path (after the content was swapped) makes Slate throw "Cannot
        // resolve a DOM point from Slate point".
        if (!editor.selection && sel && Range.isRange(sel)) {
            if (Node.has(editor, sel.anchor.path) && Node.has(editor, sel.focus.path)) {
                Transforms.select(editor, sel);
            }
        }
    }, [editor]);

    const onBlur = React.useCallback(() => {
        setFocused(false);
        savedSelection.current = editor.selection;
    }, [editor]);

    const focusEditor = React.useCallback(
        (e: React.MouseEvent) => {
            if (e.target === divRef.current) {
                ReactEditor.focus(editor);
                e.preventDefault();
            }
        },
        [editor]
    );

    const onKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const keys = {
            Enter: () => {
                const options: Partial<SlateElement> = {
                    type: 'paragraph',
                    children: [{ text: '' }],
                };
                const [idx] = editor?.selection?.anchor?.path || [];
                const prevChild: any = editor?.children?.[idx === 0 ? idx : idx - 1];
                console.log('onKeyUp', editor);
                if (prevChild?.type === 'page-break') {
                    Transforms.setNodes(editor, options);
                }
            },
        };
        keys?.[event?.key as keyof typeof keys]?.();
    };
    const insertNode = React.useCallback(
        (node: any) => {
            // The snippet dropdown + "Einfügen" button steal focus, so
            // editor.selection is usually null by the time we get here. Insert
            // at the cursor captured on blur (savedSelection) instead of
            // letting Slate default to the document end — which placed the
            // snippet far from the cursor and made the view jump to the end
            // when the user clicked back in. Guard the target against the
            // current document; fall back to the end only if nothing valid is
            // available. (UCPWO-382)
            const target = editor.selection ?? savedSelection.current;
            if (
                target &&
                Range.isRange(target) &&
                Node.has(editor, target.anchor.path) &&
                Node.has(editor, target.focus.path)
            ) {
                Transforms.select(editor, target);
            } else {
                Transforms.select(editor, Editor.end(editor, []));
            }
            Transforms.insertNodes(editor, node);
            // Keep savedSelection in sync with the post-insert cursor so a later
            // focus restore doesn't point at a stale pre-insert path.
            savedSelection.current = editor.selection;
        },
        [editor]
    );
    // Kein hartes Abschneiden am Klartext-Limit mehr: maßgeblich ist die serialisierte
    // JSON-Länge (maxSerializedLength), die yup-Rule blockt das Speichern weich
    // (ADR-0004). Abschneiden würde Inhalt verwerfen, obwohl z.B. 8500 Klartextzeichen
    // in VARCHAR(10000) noch passen können.
    const onSlateChange = (newValue: Descendant[]) => {
        setValue(newValue);
    };

    // Persistenzlimit: Richtext wird als serialisiertes JSON in einer VARCHAR-Spalte
    // gespeichert — Absätze/Formatierung zählen mit, der Klartext-Zähler kann also grün
    // sein, während das Speicherlimit schon gerissen ist. Inhalt wird nie abgeschnitten;
    // das Speichern blockiert die Formular-Validierung (ADR-0004, UCPWO-396).
    const currentSerializedLength = maxSerializedLength ? JSON.stringify(value).length : 0;
    const isOverSerializedLimit =
        maxSerializedLength && currentSerializedLength > maxSerializedLength;
    // Zähler-UX: Auslastung des Speicherbudgets in Prozent — ein Klartext-Zähler
    // (x/8000) täuscht, weil Absätze/Formatierung im serialisierten JSON mitzählen
    const serializedPercent = maxSerializedLength
        ? serializedBudgetPercent(value, maxSerializedLength)
        : 0;
    // Füllfarbe direkt als Token statt colorScheme: im Normalzustand die
    // Markenfarbe (ucpw-brand, wie die Primary-Buttons), über dem Limit rot.
    const budgetBarColor = isOverSerializedLimit ? 'red.500' : 'ucpw-brand';

    const disabledProps = isDisabled
        ? {
              borderColor: '#F1F4F6',
              pointerEvents: canBeCopied ? 'auto' : 'none',
              color: 'blackAlpha.700',
              cursor: canBeCopied ? 'auto' : 'not-allowed',
              bg: '#F2F2F2',
          }
        : {
              borderColor: hasError ? 'red.500' : '#ccc',
              pointerEvents: 'auto',
              color: 'revert',
              bg: 'revert',
          };

    return (
        <Box ref={divRef} onMouseDown={focusEditor}>
            <Slate editor={editor} value={value} onChange={onSlateChange}>
                <Toolbar
                    isDisabled={isDisabled}
                    hasSnippets={hasSnippets}
                    insertNode={insertNode}
                />
                <Box cursor={isDisabled ? 'not-allowed' : 'revert'}>
                    <Box
                        sx={{
                            ul: { marginLeft: '20px' },
                            ol: { marginLeft: '20px' },
                        }}
                        borderWidth={hasError ? 2 : 1}
                        borderRadius={6}
                        p={2}
                        pb={maxSerializedLength ? 3 : 2}
                        mt={2}
                        minH={100}
                        position="relative"
                        {...disabledProps}
                        {...props}
                    >
                        <Editable
                            renderElement={renderElement}
                            renderLeaf={renderLeaf}
                            onFocus={onFocus}
                            placeholder="Bitte geben sie hier Text ein…"
                            onBlur={onBlur}
                            onKeyUp={onKeyUp}
                            // Disable Slate's default scroll-selection-into-view.
                            // For a collapsed selection on a freshly created empty
                            // node (e.g. right after pressing Enter), Chromium
                            // returns a zero rect for getBoundingClientRect
                            // (crbug 435438), so the default handler scrolls to
                            // top — jumping the view to the start of the editor.
                            // This field grows with its content and has no own
                            // scroll container, so the browser keeps the caret
                            // visible natively; no Slate auto-scroll is needed.
                            // (UCPWO-382)
                            scrollSelectionIntoView={() => {}}
                            spellCheck
                        />
                        {maxSerializedLength ? (
                            <Progress
                                value={Math.min(serializedPercent, 100)}
                                sx={{ '& > div': { bg: budgetBarColor } }}
                                size="xs"
                                bg="transparent"
                                position="absolute"
                                bottom={1}
                                left={2}
                                right={2}
                                borderRadius="full"
                                aria-label="Auslastung des Speicherbudgets"
                                title={`${serializedPercent} % des Speicherbudgets belegt`}
                                data-test-id="rte-serialized-percent"
                            />
                        ) : null}
                    </Box>
                </Box>
            </Slate>
        </Box>
    );
}

export { RichTextEditor };
